import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { formatIsoDuration } from "utils/dateUtils";
import CustomScrollbars from "../CustomScrollbars";
import RunStatusChip from "./RunStatusChip";
import RunTimestamp from "./RunTimestamp";
import StageChip from "./StageChip";
import { HEADER_CELL_SX, NO_WRAP_CELL_SX } from "./tableStyles";
import {
  timeHeaderSuffix,
  type TimezoneDisplayMode,
} from "features/integrationsBeta/timezoneDisplay";
import type {
  StepExecutionItem,
  StepExecutionSortField,
} from "features/integrationsBeta/integrationsBeta";

type Props = {
  steps: StepExecutionItem[];
  selectedStepExecutionId?: number | null;
  onSelectStep: (step: StepExecutionItem) => void;
  sortBy?: StepExecutionSortField;
  sortOrder: "asc" | "desc";
  onSortChange: (field: StepExecutionSortField) => void;
  statusLabel: (value: string | null | undefined) => string;
  stageLabel: (value: string | null | undefined) => string;
  timezoneMode: TimezoneDisplayMode;
  displayZone?: string;
};

export default function StepExecutionsTable({
  steps,
  selectedStepExecutionId,
  onSelectStep,
  sortBy,
  sortOrder,
  onSortChange,
  statusLabel,
  stageLabel,
  timezoneMode,
  displayZone,
}: Props) {
  const sortHeader = (
    field: StepExecutionSortField,
    label: string,
    align?: "left" | "center" | "right",
  ) => (
    <TableCell sx={HEADER_CELL_SX} align={align}>
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortOrder : "asc"}
        onClick={() => onSortChange(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const timeSuffix = timeHeaderSuffix(timezoneMode);
  const timeLabel = (label: string) =>
    timeSuffix ? `${label} · ${timeSuffix}` : label;
  const showCellAbbreviation = timezoneMode === "client";

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 220,
        overflow: "clip",
      }}
    >
      <CustomScrollbars
        sx={{ flex: 1, minWidth: 0, overflowX: "auto" }}
        trackOffsetTop={40}
      >
        <Table
          stickyHeader
          size="small"
          sx={{ minWidth: 800, borderColor: "divider" }}
        >
          <TableHead>
            <TableRow>
              {sortHeader("stage", "Stage")}
              {sortHeader("stage_order", "Order", "center")}
              {sortHeader("step_name", "Name")}
              {sortHeader("started_at", timeLabel("Started"))}
              {sortHeader("finished_at", timeLabel("Finished"))}
              {sortHeader("duration", "Duration")}
              {sortHeader("status", "Status")}
            </TableRow>
          </TableHead>
          <TableBody>
            {steps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 4, borderBottom: "none" }}>
                  <Typography variant="body2" color="text.secondary">
                    No step executions recorded for this run
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              steps.map((step) => {
                const isSelected = selectedStepExecutionId === step.id;
                const skipped = step.status === "SKIPPED";
                return (
                  <TableRow
                    key={step.id}
                    selected={isSelected}
                    onClick={() => onSelectStep(step)}
                    sx={{
                      cursor: "pointer",
                      "&:last-child td": { borderBottom: "none" },
                      "&:hover": {
                        bgcolor: "calibrate.primarySoft06",
                      },
                    }}
                  >
                    <TableCell>
                      <StageChip
                        stage={step.stage}
                        label={stageLabel(step.stage)}
                      />
                    </TableCell>
                    <TableCell align="center">{step.stage_order}</TableCell>
                    <TableCell sx={NO_WRAP_CELL_SX}>{step.step_name}</TableCell>
                    <TableCell sx={NO_WRAP_CELL_SX}>
                      {skipped ? (
                        "—"
                      ) : (
                        <RunTimestamp
                          iso={step.started_at}
                          timeZone={displayZone}
                          showAbbreviation={showCellAbbreviation}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={NO_WRAP_CELL_SX}>
                      {!skipped && step.finished_at ? (
                        <RunTimestamp
                          iso={step.finished_at}
                          timeZone={displayZone}
                          showAbbreviation={showCellAbbreviation}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={NO_WRAP_CELL_SX}>
                      {skipped
                        ? "—"
                        : formatIsoDuration(step.started_at, step.finished_at)}
                    </TableCell>
                    <TableCell>
                      <RunStatusChip
                        status={step.status}
                        label={statusLabel(step.status)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
    </Paper>
  );
}
